import type { Coding } from '@medplum/fhirtypes'
import type { DerivationRule } from '#src/observation-logic/derivations/rules.ts'
import {
  type CompletenessPolicy,
  type ScoreBand,
  sumScoresRule,
} from '#src/observation-logic/derivations/scores.ts'
import { INSTRUMENT_BANDS_SYSTEM } from '#src/terminology/instruments/instrumentTerminology.ts'
import type {
  InstrumentItem,
  InstrumentScore,
  InstrumentSection,
} from '#src/terminology/instruments/types.ts'
import { ASSESSMENT_QUESTIONS_SYSTEM } from '#src/terminology/types.ts'

export function ownCoding(code: string, display: string): Coding {
  return { system: ASSESSMENT_QUESTIONS_SYSTEM, code, display }
}

export function scoredItem(
  coding: Coding,
  answerSet: string,
  extra: Partial<InstrumentItem> = {}
): InstrumentItem {
  return {
    coding,
    label: coding.display ?? coding.code ?? '',
    scaleType: 'Ord',
    answerSet,
    required: true,
    scored: true,
    ...extra,
  }
}

// A domain subscore the clinician enters as a number within `range`, counted
// toward the total as itself.
export function numberItem(
  coding: Coding,
  range: { min: number; max: number },
  extra: Partial<InstrumentItem> = {}
): InstrumentItem {
  return {
    coding,
    label: coding.display ?? coding.code ?? '',
    scaleType: 'Qn',
    range,
    required: true,
    scored: true,
    ...extra,
  }
}

export function scoreBand(
  range: { min?: number; max?: number },
  code: string,
  display: string,
  severity: ScoreBand['severity'],
  when?: ScoreBand['when']
): ScoreBand {
  const band: ScoreBand = {
    ...range,
    code: { system: INSTRUMENT_BANDS_SYSTEM, code, display },
    display,
    severity,
  }
  if (when) {
    band.when = when
  }
  return band
}

export interface TotalScoreOptions {
  templateId: string
  coding: Coding
  sections: InstrumentSection[]
  completeness: CompletenessPolicy
  bands: ScoreBand[]
  // Wraps the plain sum, for a tool whose total needs a particular item.
  rule?: (sum: DerivationRule) => DerivationRule
}

// The sum of every scored item's answer score.
export function totalScore({
  templateId,
  coding,
  sections,
  completeness,
  bands,
  rule = (sum) => sum,
}: TotalScoreOptions): InstrumentScore {
  return {
    coding,
    label: coding.display ?? 'Total score',
    unit: '{score}',
    rule: rule(
      sumScoresRule({
        id: `${templateId}-total`,
        target: coding,
        items: sections
          .flatMap((section) => section.items)
          .filter((item) => item.scored)
          .map((item) => item.coding),
        policy: completeness,
      })
    ),
    bands,
    completeness,
  }
}
